import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  DineInSession,
  DineInSessionMember,
  DineInRound,
  DineInRoundItem,
  DineInPhase,
} from "../../interfaces/DineInObject";
import { ProductDetailsModal } from "../../interfaces/ProductObject";
import type { Order } from "../../interfaces/OrderObject";
import {
  fetchDineInProducts,
  openTable,
  joinTable,
  submitRound,
  addDineInItems,
  generateBill,
  refreshDineInSession,
  DineInErrorPayload,
} from "../../services/thunks/DineInThunk";
import { DINE_IN_ERROR_CODES, friendlyDineInMessage } from "../../services/apiServices/DineInApiService";
import {
  setStoredDineInCode,
  clearStoredDineInCode,
  getActiveDineInSession,
  setActiveDineInSession,
  clearActiveDineInSession,
  ActiveDineInSession,
} from "../../services/helpers/DineInStorageHelper";
import { ConvertedDineInSession } from "../../services/converters/backendToUi/dineInConvertor";
import { resetAllState } from "./resetAllState";

/** Dev-only fixture shape for loadMockSession — see dineInMockData.ts. */
export type DineInMockSeed = ConvertedDineInSession & { products: ProductDetailsModal[] };

/** Unwraps DineInErrorPayload down to the plain code, whichever shape a
 *  rejected action came back with. */
function payloadCode(payload: DineInErrorPayload): string {
  return typeof payload === "string" ? payload : payload.code;
}

/** The host's name, when the payload carried one (see toDineInErrorPayload
 *  in DineInThunk.ts) — undefined for the plain-string shape. */
function payloadHostName(payload: DineInErrorPayload): string | undefined {
  return typeof payload === "string" ? undefined : payload.hostName;
}

interface DineInState {
  /** Drives which screen DineIn.tsx renders. See DineInPhase for the meaning
   *  of each value. */
  phase: DineInPhase;
  session: DineInSession | null;
  members: DineInSessionMember[];
  rounds: DineInRound[];
  roundItems: DineInRoundItem[];
  /** Resolved product details for every productId referenced by roundItems. */
  products: ProductDetailsModal[];
  /** Inline "that code is wrong" message for DineInCodeEntry — kept separate
   *  from errorMap.joinTable, which is reserved for non-recoverable failures. */
  codeError: string | null;
  /** Whoever opened the table, when the backend's told us — sourced from
   *  INVALID_OR_MISSING_SESSION_CODE's additionalDetails.hostName on either
   *  the bootstrap openTable call or a failed joinTable attempt. Null until
   *  we hear it, so DineInCodeEntry falls back to "the host". */
  hostName: string | null;
  /** Mirrors DineInStorageHelper's global active-session marker into the
   *  store so components outside the /dine-in/ route (the Products page) can
   *  react to it via useSelector instead of reading localStorage directly.
   *  Null means "not currently part of any active dine-in session". */
  activeSession: ActiveDineInSession | null;
  /** The dine-in order produced by billing THIS client's session; shown as a
   *  receipt. Lives here (not in a component) so it survives re-renders and
   *  phase changes caused by live pushes. Null until billed. */
  bill: Order | null;
  loadingMap: Record<string, string | null>;
  errorMap: Record<string, string | null>;
  successMap: Record<string, boolean>;
}

const initialState: DineInState = {
  phase: "bootstrapping",
  session: null,
  members: [],
  rounds: [],
  roundItems: [],
  products: [],
  codeError: null,
  hostName: null,
  activeSession: getActiveDineInSession(),
  bill: null,
  loadingMap: {},
  errorMap: {},
  successMap: {},
};

/**
 * Applies a fulfilled openTable/joinTable/submitRound/addDineInItems
 * response to state — shared across all four thunks since they hit the same
 * endpoint family and return the same session envelope shape. Also persists
 * whatever code the backend confirmed (so a later re-scan/reload skips
 * straight past the code screen) and marks this session as the one the user
 * is actively part of, so pages outside /dine-in/ (the Products tab) know to
 * offer "add to dine-in session" for this business.
 */
function applySessionResponse(state: DineInState, payload: ConvertedDineInSession): void {
  state.session = payload.session;
  state.members = payload.members;
  state.rounds = payload.rounds;
  state.roundItems = payload.roundItems;
  setStoredDineInCode(
    payload.session.businessId,
    String(payload.session.tableNumber),
    payload.session.sessionCode
  );
  const activeSession: ActiveDineInSession = {
    businessId: payload.session.businessId,
    tableNumber: String(payload.session.tableNumber),
    dineInId: payload.session.sessionId,
  };
  setActiveDineInSession(activeSession);
  state.activeSession = activeSession;
}

const dineInSlice = createSlice({
  name: "dineIn",
  initialState,
  reducers: {
    setSession(state, action: PayloadAction<DineInSession>) {
      state.session = action.payload;
    },
    setMembers(state, action: PayloadAction<DineInSessionMember[]>) {
      state.members = action.payload;
    },
    addMember(state, action: PayloadAction<DineInSessionMember>) {
      const exists = state.members.some(
        (m) => m.memberId === action.payload.memberId
      );
      if (!exists) state.members.push(action.payload);
    },
    setRounds(state, action: PayloadAction<DineInRound[]>) {
      state.rounds = action.payload;
    },
    setRoundItems(state, action: PayloadAction<DineInRoundItem[]>) {
      state.roundItems = action.payload;
    },
    addRoundItem(state, action: PayloadAction<DineInRoundItem>) {
      state.roundItems.push(action.payload);
    },
    setProducts(state, action: PayloadAction<ProductDetailsModal[]>) {
      state.products = action.payload;
    },
    /** Clears the inline wrong-code message — call when the user edits the
     *  code field again after a failed join attempt. */
    clearCodeError(state) {
      state.codeError = null;
    },
    /**
     * Founder taps "Continue to the table" on the reveal screen. No
     * dedicated backend action for this — the session/rounds/members are
     * already fully loaded from the openTable response, so this is a
     * client-only phase transition.
     */
    acknowledgeCodeRevealed(state) {
      if (state.phase === "revealCode") state.phase = "inSession";
    },
    /**
     * DEV-ONLY: loads a full session/round/product fixture straight into
     * state for UI work on DineInSession without a live backend. Dispatched
     * only from DineIn.tsx's `?mock=1` branch, itself gated on
     * import.meta.env.DEV — unreachable in a production build. Deliberately
     * does NOT touch local storage (unlike applySessionResponse), so it
     * can't leave a fake code behind that later confuses a real openTable
     * call against the same businessId/tableNumber.
     */
    loadMockSession(state, action: PayloadAction<DineInMockSeed>) {
      state.session = action.payload.session;
      state.members = action.payload.members;
      state.rounds = action.payload.rounds;
      state.roundItems = action.payload.roundItems;
      state.products = action.payload.products;
      state.phase = "inSession";
      state.codeError = null;
      state.errorMap.openTable = null;
      state.errorMap.joinTable = null;
      state.loadingMap.openTable = null;
      state.loadingMap.joinTable = null;
    },
    /** Leaving the table for good — clears the stored code and the active-
     *  session marker so a future scan starts fresh and the Products page
     *  stops offering "add to dine-in session" for this table. */
    clearSession(state) {
      if (state.session) {
        clearStoredDineInCode(state.session.businessId, String(state.session.tableNumber));
      }
      clearActiveDineInSession();
      return { ...initialState, activeSession: null };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDineInProducts.pending, (state) => {
        state.loadingMap.fetchDineInProducts = "Loading menu items...";
        state.errorMap.fetchDineInProducts = null;
      })
      .addCase(fetchDineInProducts.fulfilled, (state, action) => {
        state.loadingMap.fetchDineInProducts = null;
        // Merge rather than replace — items may be fetched incrementally as
        // new products get added across rounds.
        const existingIds = new Set(state.products.map((p) => p.productId));
        for (const product of action.payload) {
          if (!existingIds.has(product.productId)) state.products.push(product);
        }
      })
      .addCase(fetchDineInProducts.rejected, (state, action) => {
        state.loadingMap.fetchDineInProducts = null;
        state.errorMap.fetchDineInProducts = action.payload as string;
      })

      // openTable — the bootstrap call DineIn.tsx fires on mount.
      .addCase(openTable.pending, (state) => {
        state.loadingMap.openTable = "Setting up your table...";
        state.errorMap.openTable = null;
      })
      .addCase(openTable.fulfilled, (state, action) => {
        state.loadingMap.openTable = null;
        applySessionResponse(state, action.payload);
        // Sent code was null -> this table's session was just created for us
        // -> reveal it. Sent a code (stored from a prior visit) -> we're
        // rejoining a session we already know about -> straight to the table.
        state.phase = action.meta.arg.sessionCode === null ? "revealCode" : "inSession";
      })
      .addCase(openTable.rejected, (state, action) => {
        state.loadingMap.openTable = null;
        const code = payloadCode(action.payload as DineInErrorPayload);
        if (code === DINE_IN_ERROR_CODES.INVALID_OR_MISSING_SESSION_CODE) {
          // Not a failure — it means a session already exists for this table.
          // If we sent a stored code, it's now proven stale; drop it so we
          // don't keep silently retrying it on every future visit.
          if (action.meta.arg.sessionCode) {
            clearStoredDineInCode(action.meta.arg.businessId, action.meta.arg.tableNumber);
          }
          const hostName = payloadHostName(action.payload as DineInErrorPayload);
          if (hostName) state.hostName = hostName;
          state.phase = "needsCode";
          return;
        }
        state.phase = "unavailable";
        state.errorMap.openTable = friendlyDineInMessage(code);
      })

      // joinTable — fired from DineInCodeEntry's "Join table" submit.
      .addCase(joinTable.pending, (state) => {
        state.loadingMap.joinTable = "Joining table...";
        state.codeError = null;
      })
      .addCase(joinTable.fulfilled, (state, action) => {
        state.loadingMap.joinTable = null;
        applySessionResponse(state, action.payload);
        state.phase = "inSession";
      })
      .addCase(joinTable.rejected, (state, action) => {
        state.loadingMap.joinTable = null;
        const code = payloadCode(action.payload as DineInErrorPayload);
        if (code === DINE_IN_ERROR_CODES.INVALID_OR_MISSING_SESSION_CODE) {
          const hostName = payloadHostName(action.payload as DineInErrorPayload);
          if (hostName) state.hostName = hostName;
          state.codeError = hostName
            ? `That code's not right — ask ${hostName} to read it out again.`
            : friendlyDineInMessage(code);
          return;
        }
        state.phase = "unavailable";
        state.errorMap.joinTable = friendlyDineInMessage(code);
      })

      // submitRound — DineInActionBar's "Order" button, closes the open round.
      .addCase(submitRound.pending, (state) => {
        state.loadingMap.submitRound = "Sending your order...";
        state.errorMap.submitRound = null;
      })
      .addCase(submitRound.fulfilled, (state, action) => {
        state.loadingMap.submitRound = null;
        applySessionResponse(state, action.payload);
      })
      .addCase(submitRound.rejected, (state, action) => {
        state.loadingMap.submitRound = null;
        const code = payloadCode(action.payload as DineInErrorPayload);
        if (
          code === DINE_IN_ERROR_CODES.NOT_A_SESSION_MEMBER ||
          code === DINE_IN_ERROR_CODES.SESSION_NOT_FOUND
        ) {
          // Backend says we've fallen out of the session mid-order, or the
          // session itself is gone — either way the stored code and the
          // active-session marker are no good anymore; send them back to rejoin.
          if (state.session) {
            clearStoredDineInCode(state.session.businessId, String(state.session.tableNumber));
          }
          clearActiveDineInSession();
          state.activeSession = null;
          state.phase = "needsCode";
          return;
        }
        state.errorMap.submitRound = friendlyDineInMessage(code);
      })

      // generateBill — closes the table. The thunk is shared with the kitchen
      // board, so only treat it as OUR session ending when the ids match.
      .addCase(generateBill.pending, (state) => {
        state.loadingMap.generateBill = "Generating your bill...";
        state.errorMap.generateBill = null;
      })
      .addCase(generateBill.fulfilled, (state, action) => {
        state.loadingMap.generateBill = null;
        if (state.session?.sessionId !== action.meta.arg.sessionId) return;
        // The session is closed server-side: its code is dead, so drop it and
        // the active marker; the next scan starts a fresh table.
        clearStoredDineInCode(state.session.businessId, String(state.session.tableNumber));
        clearActiveDineInSession();
        state.activeSession = null;
        state.bill = action.payload;
      })
      .addCase(generateBill.rejected, (state, action) => {
        state.loadingMap.generateBill = null;
        state.errorMap.generateBill = (action.payload as string) ?? "Couldn't generate the bill.";
      })

      // addDineInItems — DineInOrderFab's "Add to dine-in session" button,
      // called from the Products page (outside the dine-in route) with
      // whatever was staged in the local collection cart.
      .addCase(addDineInItems.pending, (state) => {
        state.loadingMap.addDineInItems = "Adding to your table...";
        state.errorMap.addDineInItems = null;
      })
      .addCase(addDineInItems.fulfilled, (state, action) => {
        state.loadingMap.addDineInItems = null;
        applySessionResponse(state, action.payload);
      })
      .addCase(addDineInItems.rejected, (state, action) => {
        state.loadingMap.addDineInItems = null;
        const code = payloadCode(action.payload as DineInErrorPayload);
        if (
          code === DINE_IN_ERROR_CODES.NOT_A_SESSION_MEMBER ||
          code === DINE_IN_ERROR_CODES.SESSION_NOT_FOUND
        ) {
          // Same handling as submitRound: we've fallen out of the session, or
          // it no longer exists — the stored code and active-session marker
          // are no good anymore, so don't keep retrying them from the
          // Products page.
          if (state.session) {
            clearStoredDineInCode(state.session.businessId, String(state.session.tableNumber));
          }
          clearActiveDineInSession();
          state.activeSession = null;
          state.phase = "needsCode";
          return;
        }
        state.errorMap.addDineInItems = friendlyDineInMessage(code);
      })

      // refreshDineInSession — silent snapshot pull from a DINE_IN_* live-
      // update push (dispatch wiring lands in a later phase; this reducer
      // works standalone in the meantime). applySessionResponse never
      // touches `phase`, so this can't accidentally bounce the user to a
      // different screen the way openTable/joinTable's fulfilled cases do.
      .addCase(refreshDineInSession.fulfilled, (state, action) => {
        applySessionResponse(state, action.payload);
      })
      // Deliberately inert — see refreshDineInSession's doc comment. No
      // phase change, no errorMap entry, no toast: a silent background
      // refresh failing should never interrupt the session.
      .addCase(refreshDineInSession.rejected, () => {})

      .addCase(resetAllState, () => initialState);
  },
});

export const {
  setSession,
  setMembers,
  addMember,
  setRounds,
  setRoundItems,
  addRoundItem,
  setProducts,
  clearCodeError,
  acknowledgeCodeRevealed,
  loadMockSession,
  clearSession,
} = dineInSlice.actions;

export const selectDineInPhase = (state: any): DineInPhase => state.dineIn.phase;

export const selectDineInSession = (state: any): DineInSession | null =>
  state.dineIn.session;

export const selectDineInMembers = (state: any): DineInSessionMember[] =>
  state.dineIn.members;

export const selectDineInRounds = (state: any): DineInRound[] => state.dineIn.rounds;

export const selectDineInRoundItems = (state: any): DineInRoundItem[] =>
  state.dineIn.roundItems;

export const selectDineInProducts = (state: any): ProductDetailsModal[] =>
  state.dineIn.products;

export const selectDineInCodeError = (state: any): string | null => state.dineIn.codeError;

/** Whoever opened the table, once the backend's told us via
 *  INVALID_OR_MISSING_SESSION_CODE's additionalDetails.hostName — null until
 *  then, so callers should fall back to a generic "the host". */
export const selectDineInHostName = (state: any): string | null => state.dineIn.hostName;

/** Which dine-in session (if any) the user is currently an active participant
 *  in — the Products page reads this to decide whether to offer "add to
 *  dine-in session" for the business currently being viewed. */
export const selectActiveDineInSession = (state: any): ActiveDineInSession | null =>
  state.dineIn.activeSession;

/** The receipt for this client's billed session, or null before billing. */
export const selectDineInBill = (state: any): Order | null => state.dineIn.bill;

export const selectDineInLoadingMap = (state: any) => state.dineIn.loadingMap;
export const selectDineInErrorMap = (state: any) => state.dineIn.errorMap;

export default dineInSlice.reducer;
